import { useState } from "react"
import { toast } from "sonner"
import type { TeamMembersBlockContent, TeamMemberItem } from "../types"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { ChevronUp, ChevronDown, Loader2, Upload, X } from "lucide-react"
import { supabase } from "@/lib/supabase"
import { uploadVersionedOrgImage } from "@/lib/uploadOrgImage"
import { useOrgStore } from "@/stores/orgStore"

const IMAGE_ACCEPT = "image/png,image/jpeg,image/svg+xml"
const IMAGE_MAX_BYTES = 2 * 1024 * 1024

export interface TeamMembersBlockEditorProps {
  content: TeamMembersBlockContent | null
  onChange: (content: TeamMembersBlockContent) => void
  block?: { id: string; section_type: string }
}

export function TeamMembersBlockEditor({
  content,
  onChange,
  block,
}: TeamMembersBlockEditorProps) {
  const { currentOrg } = useOrgStore()
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null)

  const c = content ?? {}
  const members = (c.members ?? []) as TeamMemberItem[]
  const layout = (c.layout ?? "grid") as "grid" | "list"
  const update = (partial: Partial<TeamMembersBlockContent>) =>
    onChange({ ...c, ...partial })

  const setMember = (
    i: number,
    field: keyof TeamMemberItem,
    val: string | undefined
  ) => {
    const next = [...members]
    if (!next[i]) next[i] = { name: "", role: "" }
    next[i] = { ...next[i], [field]: val }
    update({ members: next })
  }

  const addMember = () =>
    update({ members: [...members, { name: "", role: "" }] })

  const removeMember = (i: number) =>
    update({ members: members.filter((_, j) => j !== i) })

  const moveMember = (from: number, direction: "up" | "down") => {
    const to = direction === "up" ? from - 1 : from + 1
    if (to < 0 || to >= members.length) return
    const next = [...members]
    ;[next[from], next[to]] = [next[to], next[from]]
    update({ members: next })
  }

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    memberIndex: number
  ) => {
    const file = e.target.files?.[0]
    if (!file || !currentOrg?.id) {
      if (!currentOrg?.id) toast.error("Organization not loaded")
      return
    }
    if (file.size > IMAGE_MAX_BYTES) {
      toast.error("Image must be 2MB or smaller")
      return
    }
    const allowed = IMAGE_ACCEPT.split(",").map((m) => m.trim())
    if (!allowed.some((m) => file.type === m)) {
      toast.error("Image must be PNG, JPEG, or SVG")
      return
    }
    setUploadingIndex(memberIndex)
    const blockId = block?.id ?? "temp"
    const { data: uploaded, error } = await uploadVersionedOrgImage(
      supabase,
      "logos",
      `${currentOrg.id}/team-members/${blockId}/${memberIndex}`,
      file
    )
    if (error || !uploaded) {
      setUploadingIndex(null)
      toast.error(error?.message ?? "Upload failed")
      return
    }
    setMember(memberIndex, "image_url", uploaded.publicUrl)
    setUploadingIndex(null)
    toast.success("Photo uploaded")
    e.target.value = ""
  }

  const handleRemoveImage = (memberIndex: number) => {
    setMember(memberIndex, "image_url", undefined)
  }

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-medium">Team Members</h3>
        <p className="text-sm text-muted-foreground">
          About page team member cards with photos, names, roles, and bios.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="team-members-heading">Heading</Label>
          <Input
            id="team-members-heading"
            value={c.heading ?? "Meet our team"}
            onChange={(e) => update({ heading: e.target.value })}
            placeholder="Meet our team"
          />
        </div>

        <div className="flex items-center gap-2">
          <Label>Layout</Label>
          <div className="flex rounded-md border p-0.5">
            <Button
              type="button"
              variant={layout === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-3"
              onClick={() => update({ layout: "grid" })}
            >
              Grid
            </Button>
            <Button
              type="button"
              variant={layout === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-3"
              onClick={() => update({ layout: "list" })}
            >
              List
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <Label>Members</Label>
          {members.map((m, i) => (
            <div
              key={i}
              className="rounded-md border p-3 space-y-2"
              data-testid={`team-member-editor-${i}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center gap-1">
                  <div className="relative">
                    {m.image_url ? (
                      <div className="relative group">
                        <img
                          src={m.image_url}
                          alt=""
                          className="h-16 w-16 rounded-full object-cover border"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(i)}
                          className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove photo"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <div
                        className="h-16 w-16 rounded-full flex items-center justify-center text-xl font-semibold bg-muted text-muted-foreground"
                        title="Upload photo"
                      >
                        {(m.name || "?")[0].toUpperCase()}
                      </div>
                    )}
                    <label className="mt-1 flex justify-center cursor-pointer">
                      <input
                        type="file"
                        accept={IMAGE_ACCEPT}
                        className="sr-only"
                        onChange={(e) => handleImageUpload(e, i)}
                        disabled={uploadingIndex !== null}
                      />
                      {uploadingIndex === i ? (
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      ) : (
                        <Upload className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                      )}
                    </label>
                  </div>
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <Input
                    placeholder="Name"
                    value={m.name}
                    onChange={(e) => setMember(i, "name", e.target.value)}
                  />
                  <Input
                    placeholder="Role"
                    value={m.role}
                    onChange={(e) => setMember(i, "role", e.target.value)}
                  />
                  <Textarea
                    placeholder="Bio (optional)"
                    value={m.bio ?? ""}
                    onChange={(e) => setMember(i, "bio", e.target.value || undefined)}
                    rows={2}
                    className="resize-none"
                  />
                  <Input
                    placeholder="Email (optional)"
                    type="email"
                    value={m.email ?? ""}
                    onChange={(e) => setMember(i, "email", e.target.value || undefined)}
                  />
                  <Input
                    placeholder="LinkedIn URL (optional)"
                    value={m.linkedin ?? ""}
                    onChange={(e) =>
                      setMember(i, "linkedin", e.target.value || undefined)
                    }
                  />
                  <div className="flex items-center gap-1 flex-wrap">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => moveMember(i, "up")}
                      disabled={i === 0}
                      aria-label="Move up"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => moveMember(i, "down")}
                      disabled={i === members.length - 1}
                      aria-label="Move down"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMember(i)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addMember}>
            Add member
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
